import React from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import Input from "../../../../../components/Input";
import Dropdown from "../../../../../components/Dropdown";
import { VALIDATION_FIELD_MAPPING, DISTANCE_FROM_LOCATION } from "../../../../../utils/appConstant";
import { Validation } from "../../../../../interfaces/common";
import { AddEdit as AddEditInterface } from "../../../../../interfaces/property.interface";

const DISTANCE_NOTE_VARIANT: Record<string, 'success' | 'warning' | 'danger'> = {
  [DISTANCE_FROM_LOCATION.le50Km]: 'danger',
  [DISTANCE_FROM_LOCATION.between50And100Km]: 'warning',
  [DISTANCE_FROM_LOCATION.gt100KmGreen]: 'success',
};

const DISTANCE_NOTE_ICON: Record<'success' | 'warning' | 'danger', string> = {
  danger: 'ri-error-warning-line',
  warning: 'ri-alert-line',
  success: 'ri-checkbox-circle-line',
};

interface AddressProps {
  countries: any[];
  states: any[];
  allCountries: any[];
  allStates: any[];
  allCities: any[];
  cities: any[];
  distanceFromLocationOptions: any[];
  formData: AddEditInterface;
  validation: Validation;
  view?: boolean;
  dropdownSearchData: any;
  onDropdownSearchChange: (e: any, name: string) => void;
  onKeyDown: (e: any) => void;
  onInputBlur: (e: any, name: string) => void;
  onInputChange: (e: any, name: string) => void;
}

const Address: React.FC<AddressProps> = ({
  countries,
  states,
  allCountries,
  allStates,
  allCities,
  cities,
  distanceFromLocationOptions,
  formData,
  validation,
  view,
  dropdownSearchData,
  onDropdownSearchChange,
  onKeyDown,
  onInputBlur,
  onInputChange
}) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="col-md-6">
        <div className="form-floating form-floating-outline">
          <Input
            type="text"
            required
            id={VALIDATION_FIELD_MAPPING.address1}
            name={VALIDATION_FIELD_MAPPING.address1}
            disabled={view}
            error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.address1)}
            errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.address1)?.message ?? ''}
            label={t("property.addEdit.pageTexts.form.address1.label")}
            placeholder={t("property.addEdit.pageTexts.form.address1.placeholder")}
            value={formData.address1}
            onKeyDown={onKeyDown}
            onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.address1)}
            onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.address1)}
          />
        </div>
      </div>
      <div className="col-md-6">
        <div className="form-floating form-floating-outline">
          <Input
            type="text"
            id={VALIDATION_FIELD_MAPPING.address2}
            name={VALIDATION_FIELD_MAPPING.address2}
            disabled={view}
            error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.address2)}
            errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.address2)?.message ?? ''}
            label={t("property.addEdit.pageTexts.form.address2.label")}
            placeholder={t("property.addEdit.pageTexts.form.address2.placeholder")}
            value={formData.address2}
            onKeyDown={onKeyDown}
            onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.address2)}
          />
        </div>
      </div>

      <div className="col-md-3">
        <div className="form-floating form-floating-outline">
          <Dropdown
            values={formData.country ? [formData.country] : []}
            options={(countries || []).map((country: any) => ({
              label: country.name,
              value: country.id
            }))}
            allOptions={(allCountries || []).map((country: any) => ({
              label: country.name,
              value: country.id
            }))}
            id={VALIDATION_FIELD_MAPPING.country}
            name={VALIDATION_FIELD_MAPPING.country}
            disabled={view}
            searchText={dropdownSearchData.country}
            onSearch={onDropdownSearchChange}
            placeholder={t("property.addEdit.pageTexts.form.country.placeholder")}
            label={t("property.addEdit.pageTexts.form.country.label")}
            required
            error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.country)}
            errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.country)?.message ?? ''}
            onChange={onInputChange}
            onBlur={onInputBlur}
          />
        </div>
      </div>
      <div className="col-md-3">
        <div className="form-floating form-floating-outline">
          <Dropdown
            values={formData.state ? [formData.state] : []}
            options={(states || []).map((state: any) => ({
              label: state.name,
              value: state.id
            }))}
            allOptions={(allStates || []).map((state: any) => ({
              label: state.name,
              value: state.id
            }))}
            id={VALIDATION_FIELD_MAPPING.state}
            name={VALIDATION_FIELD_MAPPING.state}
            disabled={view}
            searchText={dropdownSearchData.state}
            onSearch={onDropdownSearchChange}
            placeholder={t("property.addEdit.pageTexts.form.state.placeholder")}
            label={t("property.addEdit.pageTexts.form.state.label")}
            required
            error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.state)}
            errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.state)?.message ?? ''}
            onChange={onInputChange}
            onBlur={onInputBlur}
          />
        </div>
      </div>
      <div className="col-md-3">
        <div className="form-floating form-floating-outline">
          <Dropdown
            values={formData.city ? [formData.city] : []}
            options={(cities || []).map(city => ({
              label: city.name,
              value: city.id
            }))}
            allOptions={(allCities || []).map(city => ({
              label: city.name,
              value: city.id
            }))}
            id={VALIDATION_FIELD_MAPPING.city}
            name={VALIDATION_FIELD_MAPPING.city}
            searchText={dropdownSearchData.city}
            onSearch={onDropdownSearchChange}
            label={t("property.addEdit.pageTexts.form.city.label")}
            placeholder={t("property.addEdit.pageTexts.form.city.placeholder")}
            required
            error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.city)}
            errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.city)?.message ?? ''}
            onChange={onInputChange}
            onBlur={onInputBlur}
            disabled={!!view}
          />
        </div>
      </div>
      <div className="col-md-3">
        <div className="form-floating form-floating-outline">
          <Input
            type="text"
            required
            id={VALIDATION_FIELD_MAPPING.postal}
            name={VALIDATION_FIELD_MAPPING.postal}
            disabled={view}
            error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.postal)}
            errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.postal)?.message ?? ''}
            label={t("property.addEdit.pageTexts.form.postal.label")}
            placeholder={t("property.addEdit.pageTexts.form.postal.placeholder")}
            value={formData.postal}
            onKeyDown={onKeyDown}
            onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.postal)}
            onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.postal)}
          />
        </div>
      </div>
      <div className="col-md-6">
        <div className="form-floating form-floating-outline">
          <Dropdown
            values={formData.distanceFromLocation ? [formData.distanceFromLocation] : []}
            options={(distanceFromLocationOptions || []).map((item: any) => ({
              label: item.name,
              value: item.id
            }))}
            allOptions={(distanceFromLocationOptions || []).map((item: any) => ({
              label: item.name,
              value: item.id
            }))}
            id={VALIDATION_FIELD_MAPPING.distanceFromLocation}
            name={VALIDATION_FIELD_MAPPING.distanceFromLocation}
            disabled={view}
            noSearch
            placeholder={t('property.addEdit.pageTexts.form.distanceFromLocation.placeholder')}
            label={t('property.addEdit.pageTexts.form.distanceFromLocation.label')}
            required
            error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.distanceFromLocation)}
            errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.distanceFromLocation)?.message ?? ''}
            onChange={onInputChange}
            onBlur={onInputBlur}
          />
        </div>
      </div>
      {formData.distanceFromLocation === DISTANCE_FROM_LOCATION.between50And100Km && (
        <div className="col-md-6">
          <div className="form-floating form-floating-outline">
            <Input
              type="text"
              required
              id={VALIDATION_FIELD_MAPPING.convenienceFee}
              name={VALIDATION_FIELD_MAPPING.convenienceFee}
              disabled={view}
              error={!!validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.convenienceFee)}
              errorMessage={validation.fields.find((field) => field.field === VALIDATION_FIELD_MAPPING.convenienceFee)?.message ?? ''}
              label={t('property.addEdit.pageTexts.form.convenienceFee.label')}
              placeholder={t('property.addEdit.pageTexts.form.convenienceFee.placeholder')}
              value={formData.convenienceFee}
              onKeyDown={onKeyDown}
              onBlur={e => onInputBlur(e, VALIDATION_FIELD_MAPPING.convenienceFee)}
              onChange={e => onInputChange(e, VALIDATION_FIELD_MAPPING.convenienceFee)}
            />
          </div>
        </div>
      )}
      {formData.distanceFromLocation && (
        <div className="col-md-12">
          <div
            className={clsx('d-flex align-items-center p-3 mb-4 rounded border', `border-${DISTANCE_NOTE_VARIANT[formData.distanceFromLocation]}`)}
            role="alert"
          >
            <i className={clsx(DISTANCE_NOTE_ICON[DISTANCE_NOTE_VARIANT[formData.distanceFromLocation]], 'fs-3 me-3', `text-${DISTANCE_NOTE_VARIANT[formData.distanceFromLocation]}`)}></i>
            <span className="text-body">
              {t(`property.addEdit.pageTexts.form.distanceFromLocation.notes.${formData.distanceFromLocation}`)}
            </span>
          </div>
        </div>
      )}
    </>
  );
};

export default Address;